import React, { useState, useEffect, useRef } from "react";
import {
  NavLink,
  useLocation,
  useSearchParams,
  useNavigate,
} from "react-router-dom";

import { useAuth } from "../../../utils/AuthContext";
import { useDocumentContext } from "../../../contexts/DocumentContext";
import DocumentUpload from "../../../modals/DocumentUpload";
import BulkUploadModal from "../../../modals/BulkUploadModal";
import TagModal from "../../../modals/TagModal";
import DeleteTagModal from "../../../modals/DeleteTagModal";
import UsageLimitModal from "../../../modals/UsageLimitModal";
import {
  getTags,
  createTag,
  updateTag,
  deleteTag,
  getUserOrganizations,
  getCurrentUser,
} from "../../../utils/Api";

import {
  Files,
  LayoutDashboard,
  User,
  Plus,
  Tag as TagIcon,
  MoreVertical,
  FilePlus,
  HelpCircle,
  Building2,
  Crown,
  Shield,
  ChevronDown,
  X,
  Upload,
  Atom,
  FlaskConical,
  FileText,
  Search,
} from "lucide-react";

interface TagType {
  id: string;
  name: string;
  color: string;
  document_count: number;
}

interface OrganizationType {
  id: string;
  name: string;
  role: string;
  created_at: string;
}

interface SideBarProps {
  onHelpCentreClick: () => void;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

const SideBar: React.FC<SideBarProps> = ({
  onHelpCentreClick,
  isMobileOpen,
  onMobileClose,
}) => {
  const navigate = useNavigate();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isNewDocDropdownOpen, setIsNewDocDropdownOpen] = useState(false);
  const newDocDropdownRef = useRef<HTMLDivElement>(null);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [tagModalMode, setTagModalMode] = useState<"create" | "edit">("create");
  const [editingTag, setEditingTag] = useState<TagType | null>(null);
  const [tags, setTags] = useState<TagType[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationType[]>([]);
  const [pagesUploadedToday, setPagesUploadedToday] = useState<number>(0);
  const [dailyLimit, setDailyLimit] = useState<number>(10);
  const [monthlyPagesUploaded, setMonthlyPagesUploaded] = useState<number>(0);
  const [monthlyLimit, setMonthlyLimit] = useState<number>(0);
  const [openMenuTagId, setOpenMenuTagId] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingTag, setDeletingTag] = useState<TagType | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUsageLimitModalOpen, setIsUsageLimitModalOpen] = useState(false);
  const [isAdminExpanded, setIsAdminExpanded] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const { profileName, backendToken, isAdmin, accountType } = useAuth();
  const { sidebarRefreshTrigger } = useDocumentContext();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const navItems = [
    {
      to: "/home/dashboard",
      icon: LayoutDashboard,
      label: "Dashboard",
    },
    {
      to: "/home/search",
      icon: Search,
      label: "Search",
    },
    {
      to: "/home/documents",
      icon: Files,
      label: "All documents",
    },
    {
      to: "/home/molecules",
      icon: Atom,
      label: "Molecules",
    },
    {
      to: "/home/reactions",
      icon: FlaskConical,
      label: "Reactions",
    },
  ];

  useEffect(() => {
    let cancelled = false;

    const loadTags = async () => {
      try {
        const response = await getTags();
        if (!cancelled) {
          setTags(response.data.tags);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load tags:", error);
        }
      }
    };

    const loadOrganizations = async () => {
      try {
        const response = await getUserOrganizations();
        if (!cancelled) {
          setOrganizations(response.data.organizations);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load organizations:", error);
        }
      }
    };

    loadTags();
    loadOrganizations();
    loadUserInfo();

    return () => {
      cancelled = true;
    };
  }, [backendToken]);

  useEffect(() => {
    let cancelled = false;

    const loadTags = async () => {
      try {
        const response = await getTags();
        if (!cancelled) {
          setTags(response.data.tags);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load tags:", error);
        }
      }
    };

    if (sidebarRefreshTrigger > 0) {
      loadTags();
    }

    return () => {
      cancelled = true;
    };
  }, [sidebarRefreshTrigger]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuTagId(null);
      }
      if (
        newDocDropdownRef.current &&
        !newDocDropdownRef.current.contains(event.target as Node)
      ) {
        setIsNewDocDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const loadTags = async () => {
    try {
      const response = await getTags();
      setTags(response.data.tags);
    } catch (error) {
      console.error("Failed to load tags:", error);
    }
  };

  const loadUserInfo = async () => {
    try {
      const response = await getCurrentUser();
      setPagesUploadedToday(response.data.pages_uploaded_today);
      setDailyLimit(response.data.daily_limit);
      setMonthlyPagesUploaded(response.data.monthly_pages_uploaded);
      setMonthlyLimit(response.data.monthly_limit);
    } catch (error) {
      console.error("Failed to load user info:", error);
    }
  };

  const handleCreateTag = async (name: string, color: string) => {
    try {
      await createTag(name, color);
      await loadTags();
    } catch (error) {
      console.error("Failed to create tag:", error);
      throw error;
    }
  };

  const handleUpdateTag = async (name: string, color: string) => {
    if (!editingTag) return;
    try {
      await updateTag(editingTag.id, name, color);
      await loadTags();
      setEditingTag(null);
    } catch (error) {
      console.error("Failed to update tag:", error);
      throw error;
    }
  };

  const openDeleteModal = (tag: TagType) => {
    setDeletingTag(tag);
    setIsDeleteModalOpen(true);
    setOpenMenuTagId(null);
  };

  const handleDeleteTag = async () => {
    if (!deletingTag) return;

    setIsDeleting(true);
    try {
      await deleteTag(deletingTag.id);
      await loadTags();
      setIsDeleteModalOpen(false);
      setDeletingTag(null);
    } catch (error) {
      console.error("Failed to delete tag:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const openEditModal = (tag: TagType) => {
    setEditingTag(tag);
    setTagModalMode("edit");
    setIsTagModalOpen(true);
    setOpenMenuTagId(null);
  };

  const openCreateModal = () => {
    setEditingTag(null);
    setTagModalMode("create");
    setIsTagModalOpen(true);
  };

  const isDailyLimitExceeded =
    (accountType === "free" || accountType === "individual") &&
    pagesUploadedToday >= dailyLimit;
  const isMonthlyLimitExceeded =
    accountType === "individual" && monthlyLimit > 0 && monthlyPagesUploaded >= monthlyLimit;
  const isLimitExceeded = isDailyLimitExceeded || isMonthlyLimitExceeded;
  const limitType: "daily" | "monthly" = isMonthlyLimitExceeded ? "monthly" : "daily";

  const displayAccountType =
    accountType === "organization"
      ? "Organisation"
      : accountType === "free"
        ? "Free"
        : accountType;

  return (
    <>
      {/* Mobile backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      <aside
        className={`!bg-[#1d365d] w-72 flex-shrink-0 flex flex-col
        fixed inset-y-0 left-0 z-50
        lg:static lg:z-auto lg:inset-auto
        transform transition-transform duration-300 ease-in-out
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        <button
          onClick={onMobileClose}
          className="lg:hidden absolute top-4 right-4 p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded-md transition-colors z-10"
        >
          <X size={18} />
        </button>

        {/* New document button */}
        <div className="px-4 pt-12 pb-3 lg:pt-4 flex-shrink-0">
          <div className="relative" ref={newDocDropdownRef}>
            <div className="flex rounded-lg border border-white/20 overflow-hidden">
              <button
                id="tour-new-document"
                onClick={() => {
                  if (isLimitExceeded) {
                    setIsUsageLimitModalOpen(true);
                  } else {
                    setIsUploadModalOpen(true);
                  }
                }}
                className="flex-1 flex items-center justify-center gap-2 text-sm font-medium text-white py-2.5 hover:bg-white/10 transition-colors"
              >
                <FilePlus size={17} />
                <span>New document</span>
              </button>
              <div className="w-px bg-white/20" />
              <button
                onClick={() => setIsNewDocDropdownOpen((o) => !o)}
                className="px-2.5 flex items-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                aria-label="More upload options"
              >
                <ChevronDown
                  size={14}
                  className={`transition-transform ${isNewDocDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>
            </div>

            {isNewDocDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                <button
                  onClick={() => {
                    setIsNewDocDropdownOpen(false);
                    if (isLimitExceeded) {
                      setIsUsageLimitModalOpen(true);
                    } else {
                      setIsUploadModalOpen(true);
                    }
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <FilePlus size={14} />
                  <span>New document</span>
                </button>
                <button
                  onClick={() => {
                    setIsNewDocDropdownOpen(false);
                    setIsBulkUploadOpen(true);
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Upload size={14} />
                  <span>Bulk upload</span>
                </button>
              </div>
            )}
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-4 pb-3 sidebar-scroll">
          <div className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isAllDocumentsLink = item.to === "/home/documents";
              const hasTagParam = searchParams.get("tag");
              const customIsActive = isAllDocumentsLink
                ? location.pathname === "/home/documents" && !hasTagParam
                : location.pathname === item.to;

              const tourId =
                item.to === "/home/dashboard"
                  ? "tour-dashboard"
                  : item.to === "/home/documents"
                    ? "tour-all-documents"
                    : undefined;

              return (
                <NavLink
                  key={item.to}
                  id={tourId}
                  to={item.to}
                  onClick={onMobileClose}
                  className={() =>
                    `flex items-center gap-3 px-3 py-3 rounded-md text-sm transition-colors ${
                      customIsActive
                        ? "bg-white/15 text-white font-medium"
                        : "text-white/60 hover:bg-white/8 hover:text-white"
                    }`
                  }
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}

            {isAdmin && (
              <div>
                <button
                  onClick={() => setIsAdminExpanded(!isAdminExpanded)}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-md text-sm transition-colors ${
                    location.pathname.startsWith("/home/admin")
                      ? "bg-white/15 text-white font-medium"
                      : "text-white/60 hover:bg-white/8 hover:text-white"
                  }`}
                >
                  <Shield size={20} />
                  <span>Admin</span>
                  <ChevronDown
                    size={13}
                    className={`ml-auto transition-transform ${isAdminExpanded ? "rotate-180" : ""}`}
                  />
                </button>
                {isAdminExpanded && (
                  <div className="ml-6 mt-0.5 space-y-0.5">
                    <NavLink
                      to="/home/admin/dashboard"
                      onClick={onMobileClose}
                      className={() =>
                        `flex items-center gap-3 px-3 py-3 rounded-md text-sm transition-colors ${
                          location.pathname === "/home/admin/dashboard"
                            ? "bg-white/15 text-white font-medium"
                            : "text-white/60 hover:bg-white/8 hover:text-white"
                        }`
                      }
                    >
                      <LayoutDashboard size={20} />
                      <span>Dashboard</span>
                    </NavLink>
                    <NavLink
                      to="/home/admin/organizations"
                      onClick={onMobileClose}
                      className={() =>
                        `flex items-center gap-3 px-3 py-3 rounded-md text-sm transition-colors ${
                          location.pathname === "/home/admin/organizations"
                            ? "bg-white/15 text-white font-medium"
                            : "text-white/60 hover:bg-white/8 hover:text-white"
                        }`
                      }
                    >
                      <Building2 size={20} />
                      <span>Organisations</span>
                    </NavLink>
                    <NavLink
                      to="/home/admin/documents"
                      onClick={onMobileClose}
                      className={() =>
                        `flex items-center gap-3 px-3 py-3 rounded-md text-sm transition-colors ${
                          location.pathname === "/home/admin/documents"
                            ? "bg-white/15 text-white font-medium"
                            : "text-white/60 hover:bg-white/8 hover:text-white"
                        }`
                      }
                    >
                      <FileText size={20} />
                      <span>Documents</span>
                    </NavLink>
                  </div>
                )}
              </div>
            )}

            <div className="pt-4 pb-1">
              <p className="px-3 text-[10px] font-medium uppercase tracking-widest text-white/35 mb-1">
                Labels
              </p>

              <div className="space-y-1">
                <button
                  id="tour-new-label"
                  onClick={openCreateModal}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-md text-sm transition-colors text-white/60 hover:bg-white/8 hover:text-white"
                >
                  <Plus size={20} />
                  <span>New label</span>
                </button>

                {tags.length > 0 &&
                  tags.map((tag) => {
                    const currentTagParam = searchParams.get("tag");
                    const isTagActive =
                      location.pathname === "/home/documents" &&
                      currentTagParam === tag.id;

                    return (
                      <div key={tag.id} className="relative group">
                        <NavLink
                          to={`/home/documents?tag=${tag.id}`}
                          onClick={onMobileClose}
                          className={() =>
                            `flex items-center gap-3 px-3 py-3 rounded-md text-sm transition-colors ${
                              isTagActive
                                ? "bg-white/15 text-white font-medium"
                                : "text-white/60 hover:bg-white/8 hover:text-white"
                            }`
                          }
                        >
                          <TagIcon size={20} style={{ color: tag.color }} />
                          <span className="truncate">{tag.name}</span>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setOpenMenuTagId(
                                openMenuTagId === tag.id ? null : tag.id,
                              );
                            }}
                            className="ml-auto opacity-0 group-hover:opacity-100 text-white/50 hover:text-white p-0.5 rounded transition-all"
                          >
                            <MoreVertical size={13} />
                          </button>
                        </NavLink>

                        {openMenuTagId === tag.id && (
                          <div
                            ref={menuRef}
                            className="absolute right-2 top-9 z-50 w-32 bg-white rounded-lg shadow-lg border border-gray-200 py-1"
                          >
                            <button
                              onClick={() => openEditModal(tag)}
                              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => openDeleteModal(tag)}
                              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-500 hover:bg-red-50"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}

                <NavLink
                  to="/home/documents?tag=uncategorized"
                  onClick={onMobileClose}
                  className={() => {
                    const currentTagParam = searchParams.get("tag");
                    const isUncategorizedActive =
                      location.pathname === "/home/documents" &&
                      currentTagParam === "uncategorized";
                    return `flex items-center gap-3 px-3 py-3 rounded-md text-sm transition-colors ${
                      isUncategorizedActive
                        ? "bg-white/15 text-white font-medium"
                        : "text-white/60 hover:bg-white/8 hover:text-white"
                    }`;
                  }}
                >
                  <TagIcon size={20} style={{ color: "#9CA3AF" }} />
                  <span>Uncategorised</span>
                </NavLink>
              </div>
            </div>

            {/* organizations */}
            {organizations.length > 0 && (
              <div className="pt-4 pb-1">
                <p className="px-3 text-[10px] font-medium uppercase tracking-widest text-white/35 mb-1">
                  Organisations
                </p>
                <div className="space-y-1">
                  {organizations.map((org) => {
                    const isOrgActive =
                      location.pathname === `/home/organization/${org.id}`;
                    return (
                      <button
                        key={org.id}
                        onClick={() => {
                          navigate(`/home/organization/${org.id}`);
                          onMobileClose();
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-3 rounded-md text-sm transition-colors ${
                          isOrgActive
                            ? "bg-white/15 text-white font-medium"
                            : "text-white/60 hover:bg-white/8 hover:text-white"
                        }`}
                      >
                        <Building2 size={20} />
                        <span className="flex items-center gap-1.5 truncate">
                          {org.name}
                          {org.role === "admin" && <Crown size={13} />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </nav>

        <div className="px-4 py-4 border-t border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
              {accountType === "free" || accountType === "individual" ? (
                <User size={20} className="text-white/80" />
              ) : (
                <Building2 size={20} className="text-white/80" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              {profileName && (
                <p className="text-sm font-medium text-white truncate leading-tight">
                  {profileName}
                </p>
              )}
              <div className="flex items-center gap-2">
                <p className="text-xs text-white/40 truncate">
                  {displayAccountType.charAt(0).toUpperCase() +
                    displayAccountType.slice(1)}
                </p>
                {accountType === "free" && (
                  <button
                    onClick={() => navigate("/upgrade")}
                    className="text-xs font-semibold text-yellow-400 hover:text-yellow-300 transition-colors flex-shrink-0"
                  >
                    Upgrade
                  </button>
                )}
              </div>
            </div>
            <button
              id="tour-help"
              type="button"
              onClick={onHelpCentreClick}
              className="p-1.5 rounded-md hover:bg-white/10 transition-colors flex-shrink-0"
            >
              <HelpCircle size={22} className="text-white/50 hover:text-white transition-colors" />
            </button>
          </div>
        </div>
      </aside>
      <DocumentUpload
        mode="create"
        isOpen={isUploadModalOpen}
        setIsOpen={setIsUploadModalOpen}
      />
      <TagModal
        isOpen={isTagModalOpen}
        setIsOpen={setIsTagModalOpen}
        onSubmit={tagModalMode === "create" ? handleCreateTag : handleUpdateTag}
        mode={tagModalMode}
        initialName={editingTag?.name || ""}
        initialColor={editingTag?.color || "#3B82F6"}
      />
      <DeleteTagModal
        tagName={deletingTag?.name || ""}
        isDeleting={isDeleting}
        onConfirmDelete={handleDeleteTag}
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeletingTag(null);
        }}
      />
      <UsageLimitModal
        isOpen={isUsageLimitModalOpen}
        setIsOpen={setIsUsageLimitModalOpen}
        dailyLimit={dailyLimit}
        limitType={limitType}
      />
      <BulkUploadModal
        isOpen={isBulkUploadOpen}
        setIsOpen={setIsBulkUploadOpen}
      />
    </>
  );
};

export default SideBar;
